import React, { useState } from 'react';
import ModalInterior from '@alltrails/shared/components/ModalInterior';
import ModalBackground from '@alltrails/shared/components/ModalBackground';
import useMultiStepModal from '@alltrails/shared/hooks/useMultiStepModal';
import { usersContentPrivacyPolicy } from 'utils/user_helpers';
import * as styles from './styles/styles.module.scss';
import DropzonePad from '../DropzonePad';
import IndividualPrivacyModal from '../../privacyPolicy/modals/IndividualPrivacyModal';
import TrackFileUpload from '../TrackFileUpload';
import TrackUploadFields from '../TrackUploadFields';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import useDropzoneUpload from '../../../hooks/useDropzoneUpload';
import useScreenLock from '../../../hooks/useScreenLock';
import { defaultHikingActivity } from '../../../utils/activity_util';
import * as formStyles from '../styles/styles.module.scss';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { updateTrack, deleteTrack } from '../../../utils/requests/track_upload_requests';
import { generateUploadModalCta } from '../../../utils/upload_helpers';
import { policyToRadioForm, VISIBILITY_PUBLIC, ACTIVITIES } from '../../../utils/privacy_policy_helpers';

const ActivityModalUpload = ({
  url,
  closeModal,
  showSmoothOption,
  trailId,
  createAsMap,
  systemOwned,
  languageRegionCode,
  onTrackAdd,
  routeMode,
  handleRouteUpload,
  exploreFlag,
  trackParams,
  disableDropzone,
  flexiblePrivacy,
  nearbyTrails,
  initialMap,
  context
}) => {
  useScreenLock();
  const editMode = trackParams !== null;
  const [activeFile, setActiveFile] = useState(null);
  const [smoothFile, toggleSmoothFile] = useState(true);
  const [uploading, toggleUploading] = useState(false);
  const [progress, updateProgress] = useState(0);
  const [map, updateMap] = useState(initialMap);
  const [errorMessage, updateErrorMessage] = useState(null);
  const { mountedModal, linkedState, goBack, navigateTo, handleCloseModal, handleOnTransitionEnd } = useMultiStepModal(
    editMode ? 'activityForm' : 'dropzonePad',
    closeModal
  );
  const [trackFormParams, updateTrackFormParams] = useState(
    trackParams || {
      name: '',
      description: '',
      rating: 0,
      trailId,
      activity: defaultHikingActivity(),
      date: new Date(), // this date will change once a file is uploaded
      isPrivate: false,
      contentPrivacy: usersContentPrivacyPolicy(context, ACTIVITIES) || VISIBILITY_PUBLIC
    }
  );

  const updateTrackForm = payload => {
    updateTrackFormParams(prevState => {
      return { ...prevState, ...payload };
    });
  };
  /* Dropzone callback start */
  const addedFileCallback = file => {
    setActiveFile(file);
    navigateTo('uploadCenter');
  };

  const handleErrorCallback = () => {
    updateErrorMessage(StringConstantsMixin.strings.PROGRESS_MESSAGES.ERROR_SAVING);
    updateProgress(0);
    toggleUploading(false);
  };

  const sendingCallback = (file, formData = new FormData()) => {
    formData.append('presentation_type', 'track');
    if (trailId) {
      formData.append('trail_id', trackFormParams.trailId);
    }

    if (createAsMap) {
      formData.append('create_as_map', createAsMap);
    }

    if (smoothFile) {
      formData.append('smooth', 'on');
    }

    if (systemOwned) {
      formData.append('system_owned', true);
    }
  };

  const removedFileCallback = file => {
    navigateTo('dropzonePad');
    setActiveFile(null);
  };

  const progressCallback = fileProgress => {
    const progressInt = fileProgress === 100 ? 99 : parseInt(fileProgress.toFixed(0));

    updateProgress(progressInt);
  };

  const handleRouteModeUpload = uploadedMap => {
    const payload = createAsMap ? uploadedMap : uploadedMap.routes[uploadedMap.routes.length - 1];
    handleRouteUpload(payload);
    navigateTo('success');
  };

  const successCallback = (file, response) => {
    const uploadedMap = response.maps[0];
    updateProgress(100);
    updateMap(uploadedMap);
    toggleUploading(false);
    if (routeMode) {
      handleRouteModeUpload(uploadedMap);
      return;
    }

    updateTrackForm({ date: new Date(uploadedMap.created_at) });
    navigateTo('activityForm');
  };
  /* Dropzone callback End */

  const [dropzone, initDropzone] = useDropzoneUpload({
    url,
    formParam: '#uploadForm',
    paramName: 'file',
    maxFiles: 1,
    maxFilesize: 256,
    acceptedFiles: '.gpx,.tpo,.fit,.tcx,.kml,.kmz',
    addedFileCallback,
    handleErrorCallback,
    sendingCallback,
    removedFileCallback,
    progressCallback,
    successCallback,
    disabled: disableDropzone
  });

  const handleDeleteSuccess = () => {
    updateMap(null);
    removedFileCallback();
  };

  const deleteRecording = () => {
    deleteTrack(map.id)
      .then(handleDeleteSuccess)
      .catch(err => console.error('ActivityUploadModal delete', err));
  };

  const removeFileAndGoBack = () => {
    deleteRecording();
    goBack();
  };

  const setGoBack = () => {
    if (linkedState.key === 'success') {
      return null;
    }

    if (linkedState.key === 'uploadCenter' && map) {
      return removeFileAndGoBack;
    }

    return goBack;
  };

  const uploadRecording = () => {
    updateErrorMessage(null);
    toggleUploading(true);
    dropzone.processQueue();
  };

  const removeFile = file => {
    dropzone.removeFile(file);

    if (map && map.id) {
      deleteRecording();
      updateProgress(0);
    }
  };

  const openDropzone = () => {
    return dropzone.hiddenFileInput.click();
  };

  const updateErrorCallback = err => {
    toggleUploading(false);
  };

  const updateSuccessCallback = maps => {
    const payload = { detail: maps[0] };
    toggleUploading(false);

    if (exploreFlag) {
      const event = new CustomEvent('addTrack', payload);
      window.document.dispatchEvent(event);
      navigateTo('success');
      return;
    }

    onTrackAdd(payload);
    editMode ? handleCloseModal() : navigateTo('success');
  };

  const updateRecording = () => {
    toggleUploading(true);
    updateTrack(map.slug, trackFormParams.trailId, trackFormParams, flexiblePrivacy)
      .then(updateSuccessCallback)
      .catch(updateErrorCallback);
  };

  const handlePrivacyTriggerClick = () => {
    navigateTo('privacyPolicy');
  };

  const baseCtas = () => {
    switch (linkedState.key) {
      case 'dropzonePad': {
        return {
          primaryCta: generateUploadModalCta(() => {}, StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.NEXT, true)
        };
      }
      case 'uploadCenter':
        return {
          primaryCta: generateUploadModalCta(uploadRecording, StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.NEXT, uploading),
          secondaryCta: generateUploadModalCta(() => removeFile(activeFile), StringConstantsMixin.strings.CANCEL, uploading)
        };
      case 'activityForm':
        return {
          primaryCta: generateUploadModalCta(
            updateRecording,
            editMode ? StringConstantsMixin.strings.UPDATE : StringConstantsMixin.strings.UPLOAD,
            uploading
          ),
          secondaryCta: generateUploadModalCta(editMode ? handleCloseModal : goBack, StringConstantsMixin.strings.CANCEL, uploading)
        };
      default:
        return null;
    }
  };

  const dropzonePad = (
    <ModalInterior
      title={StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_RECORDING}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.dropContainer}>
        <span className={styles.uploadForm} data-testid="uploadForm">
          <DropzonePad variant="tracks" openDropzone={openDropzone} />
        </span>
      </div>
    </ModalInterior>
  );

  const uploadCenter = (
    <ModalInterior
      title={StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_RECORDING}
      description={StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.stepText(1, 2)}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.dropContainer}>
        {activeFile && (
          <>
            <TrackFileUpload
              file={activeFile}
              removeFile={removeFile}
              showSmoothOption={showSmoothOption}
              smoothFile={smoothFile}
              handleCheckboxChange={() => toggleSmoothFile(prevState => !prevState)}
              progress={progress}
              uploaded={map && map.id}
              uploading={uploading}
              flexiblePrivacy={flexiblePrivacy}
            />
            {errorMessage && <div className={formStyles.serverError}>{errorMessage}</div>}
          </>
        )}
      </div>
    </ModalInterior>
  );

  const activityForm = (
    <ModalInterior
      title={editMode ? 'Edit activity' : StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_RECORDING}
      description={!editMode && StringConstantsMixin.strings.REVIEW_UPLOAD_FORM.stepText(2, 2)}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <TrackUploadFields
        {...trackFormParams}
        updateTrackForm={updateTrackForm}
        locale={languageRegionCode}
        handlePrivacyTriggerClick={handlePrivacyTriggerClick}
        flexiblePrivacy={flexiblePrivacy}
        nearbyTrails={nearbyTrails}
        className={styles.trackUploadFields}
        trailId={trackFormParams.trailId}
      />
    </ModalInterior>
  );

  const privacyPolicy = (
    <IndividualPrivacyModal
      {...policyToRadioForm('activity', trackFormParams.contentPrivacy)}
      goBack={setGoBack()}
      closeModal={handleCloseModal}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      acceptCallback={contentPrivacy =>
        updateTrackFormParams(prevState => {
          return { ...prevState, contentPrivacy };
        })
      }
    />
  );

  const success = (
    <ModalInterior
      goBack={setGoBack()}
      closeModal={handleCloseModal}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.trailFormSuccessContainer}>
        <TrailFormSuccess variant={routeMode ? 'route' : 'track'} />
      </div>
    </ModalInterior>
  );

  const modals = {
    dropzonePad,
    uploadCenter,
    activityForm,
    privacyPolicy,
    success
  };

  return (
    <div id="uploadForm">
      <ModalBackground closeModal={handleCloseModal} goBack={setGoBack()}>
        {modals[linkedState.key]}
      </ModalBackground>
    </div>
  );
};

ActivityModalUpload.defaultProps = {
  url: '/api/alltrails/maps/gpx',
  showSmoothOption: true,
  trailId: null,
  onTrackAdd: () => {},
  exploreFlag: false,
  routeMode: false,
  handleRouteUpload: () => {},
  createAsMap: false,
  systemOwned: false,
  trackParams: null,
  disableDropzone: false,
  flexiblePrivacy: true,
  nearbyTrails: [],
  initialMap: null
};

export default ActivityModalUpload;
